/**
 * Profile API client. Auth is via Better Auth's HttpOnly session cookie,
 * which the browser attaches automatically on same-origin requests — so
 * every fetch here just needs `credentials: 'include'` (defaulted by the
 * helpers below) and no explicit Authorization header.
 */

const API = process.env.NEXT_PUBLIC_API_URL ?? ''

export interface Profile {
  user_id: number
  email: string
  full_name?: string | null
  role: string
  member_since?: string | null
  is_admin: boolean
  display_name?: string | null
  role_title?: string | null
  affiliation?: string | null
  bio?: string | null
  avatar_url?: string | null
}

export type EditableProfileField =
  | 'display_name'
  | 'role_title'
  | 'affiliation'
  | 'bio'

export const PROFILE_LIMITS: Record<EditableProfileField, number> = {
  display_name: 60,
  role_title: 80,
  affiliation: 120,
  bio: 280,
}

async function jsonOrThrow<T>(r: Response): Promise<T> {
  if (!r.ok) {
    let detail = `HTTP ${r.status}`
    try {
      const j = await r.json()
      detail = j?.detail || j?.message || detail
    } catch {
      /* ignore — keep the generic */
    }
    throw new Error(detail)
  }
  return (await r.json()) as T
}

export async function fetchProfile(): Promise<Profile> {
  const r = await fetch(`${API}/api/users/me/profile`, {
    credentials: 'include',
  })
  return jsonOrThrow<Profile>(r)
}

export async function patchProfile(
  patch: Partial<Pick<Profile, EditableProfileField>>,
): Promise<Profile> {
  const r = await fetch(`${API}/api/users/me/profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  return jsonOrThrow<Profile>(r)
}

export async function uploadAvatar(file: File): Promise<Profile> {
  const form = new FormData()
  form.append('file', file)
  const r = await fetch(`${API}/api/users/me/avatar`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  })
  return jsonOrThrow<Profile>(r)
}

export async function deleteAvatar(): Promise<Profile> {
  const r = await fetch(`${API}/api/users/me/avatar`, {
    method: 'DELETE',
    credentials: 'include',
  })
  return jsonOrThrow<Profile>(r)
}

/** Compute initials from any combination of display_name, full_name, email. */
export function initialsFor(
  p: Pick<Profile, 'display_name' | 'full_name' | 'email'>,
): string {
  const source =
    (p.display_name && p.display_name.trim()) ||
    (p.full_name && p.full_name.trim()) ||
    (p.email && p.email.split('@')[0]) ||
    '?'
  const parts = source.replace(/[._-]+/g, ' ').split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
